import { Injectable } from '@angular/core';
import { RealTimeDesktopNotificationsService } from '../real-time-desktop-notifications/real-time-desktop-notifications.service';
import { AuthService } from 'app/services/authentication/auth.service';
import { of, Subscription } from 'rxjs';
import { filter, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { TimberService } from '../../logging/timber.service';
import { RealTimeUpdateSimulationService } from '../real-time-update-simulation/real-time-update-simulation.service';
import { ShellContextService } from '../../shell-context/shell-context.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { FeatureToggle } from '@shared/enums';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { BaseSignalR } from '../signalR/base-signalR';

@Injectable({
	providedIn: 'root',
})
export class RealTimeUpdateInitializerService {
	private trackLogInSubscription: Subscription;
	private trackLogOutSubscription: Subscription;

	constructor(
		private desktopNotificationsService: RealTimeDesktopNotificationsService,
		private realTimeUpdateSimulationService: RealTimeUpdateSimulationService,
		private authService: AuthService,
		private timberService: TimberService,
		private shellContextService: ShellContextService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private globalSettingsService: GlobalSettingsService
	) { }

	trackAuthentication(): void {
		this.trackLogIn();
		this.trackLogOut();
	}

	private trackLogIn(): void {
		if (this.trackLogInSubscription) {
			return;
		}
		this.trackLogInSubscription = this.authService.authStatus$
			.pipe(
				startWith(null),
				pairwise(),
				filter(([prev, curr]) => prev?.isAuthenticated !== true && curr?.isAuthenticated === true),
				switchMap(() => {
					const useNewToken = this.featuresToggleSettingsService.isVisibleFeature(
						FeatureToggle.UseNewTokenInNotification,
						false
					);

					if (useNewToken) {
						return this.shellContextService.getContext().pipe(take(1));
					} else {
						const settingsExist = !!this.globalSettingsService.get();
						return settingsExist
							? of(this.globalSettingsService.get())
							: this.globalSettingsService.initializeCalled$.pipe(
								take(1),
								switchMap(() => of(this.globalSettingsService.get()))
							);
					}
				}),
				filter((data) => {
					const useNewToken = this.featuresToggleSettingsService.isVisibleFeature(
						FeatureToggle.UseNewTokenInNotification,
						false
					);

					if (useNewToken) {
						return !!data && 'security' in data && !!data.security?.accessToken;
					} else {
						return !!data && 'jwtAccessToken' in data && !!data.jwtAccessToken;
					}
				}),
				tap((_) => {
					this.desktopNotificationsService.checkPermissionAndStartNotifications();
					this.realTimeUpdateSimulationService.checkPermissionsAndStartRealUpdateSimulation();
				})
			)
			.subscribe(
				(_) => { },
				(error) => this.logErrorWithMessage('unhandled error in notification initializer: ', error)
			);
	}

	private trackLogOut(): void {
		if (this.trackLogOutSubscription) {
			return;
		}

		this.trackLogOutSubscription = this.authService.authStatus$
			.pipe(
				startWith(null),
				pairwise(),
				filter(([prev, curr]) => prev?.isAuthenticated === true && curr?.isAuthenticated === false),
				switchMap(async (_) => {
					await BaseSignalR.stopAll();
				})
			)
			.subscribe();
	}

	private logErrorWithMessage(message: string, error?: Error) {
		if (this.timberService.loggerTimberInitialized)
			this.timberService.error(message, { error, module: 'DesktopNotificationsInitializerService' });
	}
}
